import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';

import {
  AppActions,
  RouterActions,
  LocalStorageService,
  WindowService,
} from '@ups/xplat/core';

import { IEmployeeInfo } from '@ups/xplat/api/dto';
import { of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { IAddressChangeRequest } from '../models';

import { UserService } from '../services';
import { AuthenticationService } from '@ups/security';
import { UserActions } from './user.actions';
import { UserState } from './user.state';

const defaultPageKey = (userId) => `${userId}.defaultPage`;

/** The Auth0-namespaced claim carrying the Software Security UserId. */
export const SECURITY_USER_ID_CLAIM =
  'http://security.universalplant.com/user-id';

/**
 * How the signed-in user's Software Security UserId should be resolved from
 * a claims object (#30531). Pure, so the rule is testable without an
 * injector or an effects harness, the same reason `selectLoginIssuer` is
 * pure.
 *
 * - `claim`: the Auth0 fast path, the claim is present.
 * - `email`: no claim, but a UPN is available. This is the NORMAL state for
 *   an Entra (MSAL) sign-in, because the claim is minted by an Auth0 action
 *   and Entra never issues it. Resolve via the by-email endpoint.
 * - `none`: nothing to resolve from. The only genuinely anomalous case, and
 *   the only one that deserves a warning.
 */
export type SecurityUserIdSource =
  | { kind: 'claim'; id: string }
  | { kind: 'email'; email: string }
  | { kind: 'none' };

export function resolveSecurityUserIdSource(
  claims: Record<string, unknown> | null | undefined
): SecurityUserIdSource {
  if (!claims) return { kind: 'none' };

  const claimId = claims[SECURITY_USER_ID_CLAIM];
  if (typeof claimId === 'string' && claimId.length > 0) {
    return { kind: 'claim', id: claimId };
  }

  // MSAL surfaces the UPN on preferred_username, with upn as the fallback
  // for B2C/guest edge cases. Same order EntraAuthenticationService uses.
  const email = claims['preferred_username'] ?? claims['upn'];
  if (typeof email === 'string' && email.trim().length > 0) {
    return { kind: 'email', email: email.trim() };
  }

  return { kind: 'none' };
}

/* eslint-disable */
@Injectable()
export class UserEffects {
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadCurrent),
      mergeMap(() =>
        this.userService.fetchMyInfo().pipe(
          mergeMap((x) => {
            console.log('[UserEffects] loadCurrent success', {
              hasInfo: !!x,
              hrRef: x?.Data?.HRRef,
              position: x?.Data?.Position ?? '',
              physicalCity: x?.Data?.PhysicalAddress?.City ?? '',
              physicalState: x?.Data?.PhysicalAddress?.State ?? '',
              mailingCity: x?.Data?.MailingAddress?.City ?? '',
              mailingState: x?.Data?.MailingAddress?.State ?? '',
            });
            return [
              UserActions.loadCurrentSuccess({ payload: x }),
              UserActions.loadDefaultPage(),
            ];
          }),
          catchError((err) => of(UserActions.loadCurrentFailed(err)))
        )
      )
    )
  );

  loadRolesIfNotLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.loadRoles),
      concatLatestFrom(() => this.store.select(UserState.selectSecurityRoles)),
      filter(([, roles]) => !roles),
      exhaustMap(() => {
        return this.userService.getRoles().pipe(
          mergeMap((x) => [UserActions.loadRolesSuccess({ payload: x })]),
          catchError((err) => of(UserActions.loadRolesFailed(err)))
        );
      })
    );
  });

  loadVacationAndSickBalance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadVacationAndSickBalance),
      switchMap((payload) =>
        this.userService.fetchVacationAndSickInfo(payload.hrRef).pipe(
          mergeMap((x) => [
            UserActions.loadVacationAndSickBalanceSuccess({
              vacationAndSickBalance: x,
            }),
          ]),
          catchError((err) =>
            of(UserActions.loadVacationAndSickBalanceFailed(err))
          )
        )
      )
    )
  );

  loadAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadAttachments),
      switchMap(() =>
        this.userService.fetchMyAttachments().pipe(
          mergeMap((x) => [UserActions.loadAttachmentsSuccess({ payload: x })]),
          catchError((err) => of(UserActions.loadAttachmentsFailed(err)))
        )
      )
    )
  );

  loadEContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadEContacts),
      switchMap((hrref) =>
        this.userService.fetchEmergencyContacts(hrref.hRRef).pipe(
          mergeMap((x: any[]) => [
            UserActions.loadEContactsSuccess({ payload: x }),
          ]),
          catchError((err) => of(UserActions.loadEContactsFailed(err)))
        )
      )
    )
  );

  updateEContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateEContact),
      mergeMap(({ contact }) =>
        this.userService.saveEmergencyContact(contact).pipe(
          mergeMap(() => {
            const actions: Array<Action> = [
              UserActions.updateEContactSuccess(),
            ];
            if (this.win.navigator.onLine) {
              // only refresh econtacts after updating if online
              actions.push(UserActions.loadEContacts({ hRRef: contact.HRRef }));
            }
            return actions;
          }),
          catchError((err) => of(UserActions.updateEContactFailed(err)))
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.update),
      switchMap(({ data }) =>
        this.userService.updateEmployeeProfileBasicDetails(data).pipe(
          mergeMap((updated) => [
            UserActions.updateSuccess({ data: updated }),
            UserActions.checkAddressChangeRequest({
              hRRef: updated.HRRef,
            }),
          ]),
          catchError((err) => of(UserActions.updateFailed(err)))
        )
      )
    )
  );

  checkAddressChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.checkAddressChangeRequest),
      switchMap((payload) =>
        this.userService.checkAddressChange(payload.hRRef).pipe(
          mergeMap((data) => [
            UserActions.checkAddressChangeRequestSuccess({ data }),
          ]),
          catchError((err) =>
            of(UserActions.checkAddressChangeRequestFailed(err))
          )
        )
      )
    )
  );

  loadMulitclass$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadMulticlass),
      switchMap(({ hRRef }) =>
        this.userService.fetchEmployeeMulitclass(hRRef).pipe(
          mergeMap((x: any[]) => [
            UserActions.loadMulticlassSuccess({ payload: x }),
          ]),
          catchError((err) => of(UserActions.loadMulticlassFailed(err)))
        )
      )
    )
  );

  submitAddressChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.submitAddressChangeRequest),
      switchMap((val) =>
        this.userService.submitAddressChangeRequest(val).pipe(
          mergeMap((res: IAddressChangeRequest) => [
            UserActions.checkAddressChangeRequestSuccess({ data: res }),
            UserActions.submitAddressChangeSuccess({ payload: res }),
          ]),
          catchError((err: any) =>
            of(UserActions.submitAddressChangeFailed(err))
          )
        )
      )
    )
  );

  navigateToDefaultPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadedDefaultPage),
      filter((x) => !!x.path),
      map((x) => {
        const basePaths = ['', '/', '/home'];
        if (
          this.win.isMobile ||
          !basePaths.includes(this.win.location.pathname)
        ) {
          // 1. mobile uses dynamic guard to control initial default page
          // 2. web should only set default page when loading domain, not when deep linking somewhere else
          return new AppActions.Noop();
        } else {
          return new RouterActions.Go({ path: [x.path] });
        }
      })
    )
  );

  loadDefaultPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadCurrentSuccess),
      distinctUntilChanged(),
      withLatestFrom(this.store.select(UserState.selectCurrent)),
      map(([action, user]) =>
        this.storageService.getItem<string>(
          defaultPageKey((<IEmployeeInfo>user)?.Auth0UserId)
        )
      ),
      mergeMap((path) => [
        UserActions.loadedDefaultPage({ path }),
        UserActions.setDefaultPage({ path }),
      ])
    )
  );

  persistDefaultPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.setDefaultPage),
        distinctUntilChanged(),
        filter((x) => !!x.path),
        withLatestFrom(this.store.select(UserState.selectCurrent)),
        tap(([action, user]) =>
          this.storageService.setItem(
            defaultPageKey((<IEmployeeInfo>user)?.Auth0UserId),
            action.path
          )
        )
      ),
    {
      dispatch: false,
    }
  );

  clearDefaultPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.clearDefaultPage),
        withLatestFrom(this.store.select(UserState.selectCurrent)),
        tap(([action, user]) =>
          this.storageService.removeItem(
            defaultPageKey((<IEmployeeInfo>user)?.Auth0UserId)
          )
        )
      ),
    {
      dispatch: false,
    }
  );

  /**
   * Resolve the Software Security UserId for the signed-in user (#30531).
   *
   * The Auth0 claim `http://security.universalplant.com/user-id` is the
   * fast path and stays first. **Entra (MSAL) sign-ins never carry it**,
   * because it is an Auth0-namespaced claim minted by an Auth0 action, so
   * this effect resolved null on every Entra session and logged the claim
   * as missing as though something had gone wrong. Nothing had: the claim
   * is not expected there. The id is resolved from the account's email
   * instead, via the endpoint the server already exposes for exactly this
   * (its comment names the "shared-lib resolver" that had never been
   * written on the client).
   *
   * Note the email fallback reads `preferred_username` then `upn`, the two
   * places MSAL surfaces a UPN, and only then gives up. A null id is a
   * legitimate outcome for an unlinked account and must not error the boot.
   */
  getLoggedInSecurityUserId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getLoggedInSecurityUserId),
      switchMap(() => this.authService.claims$),
      switchMap((claims) => {
        const source = resolveSecurityUserIdSource(claims);
        switch (source.kind) {
          case 'claim':
            return of(source.id);
          case 'email':
            return this.userService.getSecurityUserIdByEmail(source.email);
          default:
            // Only genuinely anomalous case: no claim AND no UPN. A missing
            // claim alone is normal on Entra and no longer warns.
            console.warn(
              '[UserEffects] no security-user-id claim and no email claim to resolve one from; security user id stays null'
            );
            return of(null);
        }
      }),
      map((id) => UserActions.setLoggedInSecurityUserId({ id })),
      catchError((err) => {
        console.log(
          '[UserEffects] Failed to get security user ID (this is expected for public routes):',
          err
        );
        return of(UserActions.setLoggedInSecurityUserId({ id: null }));
      })
    )
  );

  loadOnLogin$ = createEffect(() =>
    this.authService.isAuthenticated$.pipe(
      filter((x) => !!x),
      tap(() => {
        console.log('[UserEffects] isAuthenticated$ true - loading user info');
      }),
      mergeMap(() => [
        UserActions.loadCurrent(),
        UserActions.getLoggedInSecurityUserId(),
      ]),
      catchError((err) => {
        // Silently catch Auth0 errors for public routes
        console.log(
          '[UserEffects] Auth check failed (this is expected for public routes):',
          err
        );
        return of(new AppActions.Noop());
      })
    )
  );

  private storageService = inject(LocalStorageService);
  private win = inject(WindowService);
  private userService = inject(UserService);

  constructor(
    private actions$: Actions,
    private store: Store,
    private authService: AuthenticationService
  ) {}
}
/* eslint-enable */
